'use client';

import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { default as AarhusCharter } from './logos/aarhuscharter.svg';
import { default as BravoTours } from './logos/bravotours.svg';
import { default as DanExplore } from './logos/danexplore.svg';
import { default as Danski } from './logos/danski.svg';
import { default as Nortlander } from './logos/nortlander.svg';
import { default as PrimoTours } from './logos/primotours.svg';
import { default as SnowTours } from './logos/snowtours.svg';
import { default as SunCharter } from './logos/suncharter.svg';
import { default as TopTours } from './logos/toptours.svg';
import styles from './image-marquee.module.scss';

export interface ImageMarqueeProps {
  /** The class name to apply */
  className?: string;
  /** Gap between images in pixels */
  gap?: number;
  /** Square size of each image in pixels */
  imageSize?: number;
  /** Renders left and right fade gradients over the edges */
  showSideGradients?: boolean;
  /** Scroll speed in pixels per second */
  speed?: number;
}

interface LogoItem {
  component: React.FC<React.SVGProps<SVGSVGElement>>;
}

const LOGOS: LogoItem[] = [
  { component: AarhusCharter },
  { component: BravoTours },
  { component: DanExplore },
  { component: Danski },
  { component: Nortlander },
  { component: PrimoTours },
  { component: SnowTours },
  { component: SunCharter },
  { component: TopTours },
];

const REVERSED_LOGOS = [...LOGOS].reverse();

const renderTrack = (items: LogoItem[], imageSize: number, copies: number) =>
  Array.from({ length: copies }, (_, copyIndex) =>
    items.map((item, index) => {
      const SvgComponent = item.component;

      return (
        <div key={`${copyIndex}-${index}`} className={styles.item}>
          <SvgComponent
            aria-hidden="true"
            height={imageSize}
            width={imageSize}
          />
        </div>
      );
    }),
  );

const ImageMarquee = ({
  className,
  gap = 32,
  imageSize = 78,
  showSideGradients = false,
  speed = 25,
}: ImageMarqueeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const setWidth = LOGOS.length * (imageSize + gap);
  // duration derived from constant speed so adding more images doesn't change perceived velocity
  const duration = setWidth / speed;
  // +1 so the set being animated out is still visible; minimum 2 to always loop
  const [copies, setCopies] = useState(2);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const computeCopies = (containerWidth: number) =>
      Math.max(2, Math.ceil(containerWidth / setWidth) + 1);

    setCopies(prev =>
      Math.max(prev, computeCopies(container.getBoundingClientRect().width)),
    );

    const observer = new ResizeObserver(([entry]) => {
      setCopies(prev => Math.max(prev, computeCopies(entry.contentRect.width)));
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [setWidth]);

  const cssVars = {
    '--image-marquee-duration': `${duration}s`,
    '--image-marquee-gap': `${gap}px`,
    '--image-marquee-set-width': `${setWidth}px`,
    '--image-marquee-size': `${imageSize}px`,
  } as React.CSSProperties;

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={classNames(styles.component, className)}
      style={cssVars}
    >
      {showSideGradients && <div className={styles.gradientLeft} />}

      {showSideGradients && <div className={styles.gradientRight} />}

      <div className={styles.row}>
        <div className={classNames(styles.track, styles.isScrollingRight)}>
          {renderTrack(LOGOS, imageSize, copies)}
        </div>
      </div>

      <div className={styles.row}>
        <div className={classNames(styles.track, styles.isScrollingLeft)}>
          {renderTrack(REVERSED_LOGOS, imageSize, copies)}
        </div>
      </div>
    </div>
  );
};

ImageMarquee.displayName = 'ImageMarquee';

export default ImageMarquee;
